import { FormEvent, useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  apiCreateRecruitmentApplication,
  type RecruitmentApplication,
  type RecruitmentPosition,
} from "@/lib/api";

interface AddCandidateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  positions: RecruitmentPosition[];
  onCreated: (application: RecruitmentApplication) => void;
}

const EMPTY_FORM = {
  positionId: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  linkedinUrl: "",
  portfolioUrl: "",
  coverLetter: "",
  notes: "",
};

export function AddCandidateDialog({
  open,
  onOpenChange,
  positions,
  onCreated,
}: AddCandidateDialogProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [resume, setResume] = useState<File | null>(null);
  const openPositions = positions.filter((position) => position.status === "open");

  useEffect(() => {
    if (!open) {
      setForm(EMPTY_FORM);
      setResume(null);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: (formData: FormData) => apiCreateRecruitmentApplication(formData),
    onSuccess: (application) => {
      toast.success(t("recruitment.applications.manual.created"));
      onCreated(application);
      onOpenChange(false);
    },
    onError: (err: any) => toast.error(
      err?.message || t("recruitment.applications.manual.createError"),
    ),
  });

  function update(field: keyof typeof EMPTY_FORM, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.positionId || !form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || !resume) {
      toast.error(t("recruitment.applications.manual.requiredError"));
      return;
    }
    if (resume.size > 10 * 1024 * 1024) {
      toast.error(t("recruitment.applications.manual.fileTooLarge"));
      return;
    }

    const data = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (value.trim()) data.append(key, value.trim());
    });
    data.append("resume", resume);
    mutation.mutate(data);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("recruitment.applications.manual.title")}</DialogTitle>
          <DialogDescription>{t("recruitment.applications.manual.description")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="manual-candidate-position">{t("recruitment.applications.position")} *</Label>
            <Select value={form.positionId} onValueChange={(value) => update("positionId", value)}>
              <SelectTrigger id="manual-candidate-position">
                <SelectValue placeholder={t("recruitment.applications.manual.selectPosition")} />
              </SelectTrigger>
              <SelectContent>
                {openPositions.map((position) => (
                  <SelectItem key={position.id} value={String(position.id)}>{position.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {openPositions.length === 0 && (
              <p className="text-xs text-amber-600">{t("recruitment.applications.manual.noOpenPositions")}</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="manual-candidate-first-name">{t("recruitment.apply.firstName")} *</Label>
              <Input
                id="manual-candidate-first-name"
                value={form.firstName}
                onChange={(event) => update("firstName", event.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="manual-candidate-last-name">{t("recruitment.apply.lastName")} *</Label>
              <Input
                id="manual-candidate-last-name"
                value={form.lastName}
                onChange={(event) => update("lastName", event.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="manual-candidate-email">{t("recruitment.apply.email")} *</Label>
              <Input
                id="manual-candidate-email"
                type="email"
                value={form.email}
                onChange={(event) => update("email", event.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="manual-candidate-phone">{t("recruitment.apply.phone")}</Label>
              <Input
                id="manual-candidate-phone"
                type="tel"
                value={form.phone}
                onChange={(event) => update("phone", event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="manual-candidate-linkedin">{t("recruitment.apply.linkedin")}</Label>
              <Input
                id="manual-candidate-linkedin"
                type="url"
                placeholder={t("recruitment.apply.linkedinPlaceholder")}
                value={form.linkedinUrl}
                onChange={(event) => update("linkedinUrl", event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="manual-candidate-portfolio">{t("recruitment.apply.portfolio")}</Label>
              <Input
                id="manual-candidate-portfolio"
                type="url"
                placeholder={t("recruitment.apply.portfolioPlaceholder")}
                value={form.portfolioUrl}
                onChange={(event) => update("portfolioUrl", event.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="manual-candidate-resume">{t("recruitment.applications.manual.cv")} *</Label>
            <label
              htmlFor="manual-candidate-resume"
              className="flex min-h-20 cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-border px-4 text-sm text-muted-foreground hover:bg-muted/40"
            >
              <Upload className="h-4 w-4" />
              <span>{resume?.name || t("recruitment.applications.manual.chooseCv")}</span>
            </label>
            <Input
              id="manual-candidate-resume"
              className="sr-only"
              type="file"
              accept=".pdf,.doc,.docx,.odt,.rtf,.txt"
              onChange={(event) => setResume(event.target.files?.[0] ?? null)}
              required
            />
            <p className="text-xs text-muted-foreground">{t("recruitment.applications.manual.cvHint")}</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="manual-candidate-cover-letter">{t("recruitment.apply.coverLetter")}</Label>
            <Textarea
              id="manual-candidate-cover-letter"
              value={form.coverLetter}
              onChange={(event) => update("coverLetter", event.target.value)}
              placeholder={t("recruitment.applications.manual.coverLetterPlaceholder")}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="manual-candidate-notes">{t("recruitment.applications.notes")}</Label>
            <Textarea
              id="manual-candidate-notes"
              value={form.notes}
              onChange={(event) => update("notes", event.target.value)}
              placeholder={t("recruitment.applications.notesPlaceholder")}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={mutation.isPending || openPositions.length === 0}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mutation.isPending
                ? t("recruitment.applications.manual.adding")
                : t("recruitment.applications.manual.add")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
